import { Navigate, useLocation, type Location } from "react-router-dom";
import { Suspense } from "react";
import { invalidateClientSession } from "@/shared/utils/authSession.util";
import { useAuth } from "@/hooks/useAuth";
import { useUserContext } from "@/context/UserContext";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  EXPO_VISITOR_HOME_PATH,
  getDefaultClientPathAfterAuth,
  hasClientCreatedProfile,
  isClientOnboardingComplete,
  canReviewGeneratedCompanyProfile,
  isDeferredProfileBrowsePath,
  isExpoSignupUser,
  needsFlexDemo,
  shouldDeferCompanyProfile,
  type UserWithCompany,
} from "@/shared/constants/clientOnboarding.const";
import { isFlexDemoViewportSupported } from "@/flex-demo/flexDemo.const";
import { isFlexDemoReplaySession } from "@/flex-demo/flexDemo.session";
import { MY_CLUSTER_PATH } from "@/pages/my-cluster/myCluster.const";
import { isClusterPortalRole } from "@/shared/utils/clusterPortalRole.util";
import { AuthRouteFallback } from "@/routes/RouteFallback";
import { SectorGuestView } from "@/routes/SectorGuestView";
import { isPublicSectorOverviewPath } from "@/routes/sectorPublicRoutes.util";
import { isCollaborationWalletPath } from "@/shared/constants/collaborationWallet.const";
import {
  guestExpoDirectoryHref,
  isClientExpoProfilePath,
} from "@/pages/discover/discoverExplore.const";
import {
  buildGuestExpoCompanyProfilePath,
  expoSlugFromCompanyProfilePath,
} from "@/pages/discover/expoGuestConnect.util";

type DecodedToken = {
  id: string;
  email: string;
  role: string;
  exp: number;
  iat: number;
};
const PROFILE_VERIFICATION_LANDING_KEY = "gt:profileVerificationSummary";

function isExpoSignupAllowedPath(path: string) {
  return (
    path.startsWith("/collaborations/") ||
    path === "/settings" ||
    path === "/account" ||
    path === "/shortlist" ||
    path === "/inquiries" ||
    path === "/updates" ||
    path === "/two-factor/setup" ||
    path === "/expos" ||
    path.startsWith("/expos/")
  );
}

/** No hooks — safe to return early on every branch (avoids Rules of Hooks issues with redirects). */
function ProtectedRouteGate({
  isAuthenticated,
  isLoading,
  userDetails,
  location,
  children,
}: {
  isAuthenticated: boolean;
  isLoading: boolean;
  userDetails: UserWithCompany | null | undefined;
  location: Location;
  children: React.ReactNode;
}) {
  const token = Cookies.get("token");
  if (token) {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      if (decoded.role === "ADMIN" || decoded.role === "SUB_ADMIN") {
        return <Navigate to="/admin/companies" replace />;
      }
      if (decoded.role === "FACILITATOR") {
        return <Navigate to="/facilitator/dashboard" replace />;
      }
      if (isClusterPortalRole(decoded.role)) {
        return <Navigate to="/ecosystem-head/dashboard" replace />;
      }
      if (decoded.role === "EXPO_HEAD") {
        return <Navigate to="/expo-head/dashboard" replace />;
      }
      if (decoded.role === "EXPERT") {
        return <Navigate to="/expert/dashboard" replace />;
      }
      // MANAGER stays in company ProtectedRoute / AppLayout
    } catch {
      // Invalid token, continue with normal flow
    }
  }

  if (!isAuthenticated && !isLoading) {
    if (isPublicSectorOverviewPath(location.pathname)) {
      return <SectorGuestView />;
    }
    const expoCompanySlug = expoSlugFromCompanyProfilePath(location.pathname);
    if (expoCompanySlug && location.pathname.startsWith("/expos/")) {
      const companyId = decodeURIComponent(location.pathname.split("/").filter(Boolean)[3] ?? "");
      if (companyId) {
        return (
          <Navigate
            to={`${buildGuestExpoCompanyProfilePath(expoCompanySlug, companyId)}${location.search}`}
            replace
            state={location.state}
          />
        );
      }
    }
    if (isClientExpoProfilePath(location.pathname)) {
      const slug = location.pathname.split("/")[2] ?? "";
      return <Navigate to={`${guestExpoDirectoryHref(decodeURIComponent(slug))}${location.search}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }

  if (isLoading || userDetails === undefined) {
    return <AuthRouteFallback />;
  }

  if (userDetails === null) {
    if (token) {
      invalidateClientSession();
    }
    return <Navigate to="/login" replace />;
  }

  const isComplete = isClientOnboardingComplete(userDetails);
  const status = userDetails.company?.status;
  const path = location.pathname;
  const profileCreated = hasClientCreatedProfile(userDetails);
  const flexDemoNeeded = needsFlexDemo(userDetails);
  const isFlexDemoPath = path === "/flex-demo" || path.startsWith("/flex-demo/");
  const flexDemoReplay = isFlexDemoReplaySession(userDetails.id);
  const expoSignupUser = isExpoSignupUser(userDetails);

  if (expoSignupUser) {
    if (path === "/my-discovery" || path.startsWith("/my-discovery/")) {
      return <Navigate to={EXPO_VISITOR_HOME_PATH} replace />;
    }
    if (path === "/create-profile" || !isExpoSignupAllowedPath(path)) {
      return <Navigate to={EXPO_VISITOR_HOME_PATH} replace />;
    }
    return <>{children}</>;
  }

  if (isFlexDemoPath && flexDemoReplay && isFlexDemoViewportSupported()) {
    return <>{children}</>;
  }

  // Profile builder is one-time onboarding — never return completed users here.
  // If the signup background job just finished, send them to review instead of Discover.
  if (profileCreated && path === "/create-profile") {
    const jobStatus = (
      userDetails.company as { websiteProfileJob?: { status?: string | null } | null } | null | undefined
    )?.websiteProfileJob?.status;
    if (jobStatus === "READY_FOR_REVIEW") {
      return <Navigate to="/profile/edit" replace />;
    }
    return <Navigate to={getDefaultClientPathAfterAuth(userDetails)} replace />;
  }

  if (!profileCreated) {
    if (shouldDeferCompanyProfile(userDetails)) {
      if (!isDeferredProfileBrowsePath(path) && !canReviewGeneratedCompanyProfile(userDetails, path)) {
        return <Navigate to="/my-discovery" replace />;
      }
      return <>{children}</>;
    }
    if (path !== "/create-profile") {
      return <Navigate to="/create-profile" replace />;
    }
    return <>{children}</>;
  }

  // Demo walkthrough is tablet+ only — phones stay in the app; provider auto-completes the assignment.
  if (flexDemoNeeded && isFlexDemoViewportSupported()) {
    if (isFlexDemoPath || path === "/two-factor/setup") {
      return <>{children}</>;
    }
    return <Navigate to="/flex-demo" replace />;
  }

  if (isFlexDemoPath) {
    if (flexDemoReplay && isFlexDemoViewportSupported()) {
      return <>{children}</>;
    }
    return <Navigate to="/my-discovery" replace />;
  }

  if (!isComplete) {
    if (status === "UNDER_REVIEW" && path === "/profile/edit") {
      sessionStorage.setItem(
        PROFILE_VERIFICATION_LANDING_KEY,
        JSON.stringify({ kind: "EDIT_LOCKED_UNDER_REVIEW" })
      );
      return <Navigate to={getDefaultClientPathAfterAuth(userDetails)} replace />;
    }

    // Onboarding "incomplete" means the company isn't verified yet, but the user should still be able to
    // access their own account areas (profile/settings/collaboration wallet/edit profile) without being trapped on
    // the discovery page.
    const allowWhileUnverified =
      path === "/my-discovery" ||
      path.startsWith("/my-discovery/") ||
      path.startsWith("/ecosystem-heads/") ||
      path === MY_CLUSTER_PATH ||
      path.startsWith(`${MY_CLUSTER_PATH}/`) ||
      path === "/expos" ||
      path.startsWith("/expos/") ||
      path === "/my-expos" ||
      path.startsWith("/sector/") ||
      path === "/profile" ||
      path === "/profile/edit" ||
      path === "/settings" ||
      path === "/account" ||
      isCollaborationWalletPath(path) ||
      path === "/invoices" ||
      path === "/invoices/wallet" ||
      path === "/two-factor/setup" ||
      path === "/home" ||
      path === "/dashboard";

    if (!allowWhileUnverified) {
      return <Navigate to={getDefaultClientPathAfterAuth(userDetails)} replace />;
    }
  }

  return <>{children}</>;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { userDetails } = useUserContext();
  const location = useLocation();

  return (
    <ProtectedRouteGate
      isAuthenticated={isAuthenticated}
      isLoading={isLoading}
      userDetails={userDetails as UserWithCompany | null | undefined}
      location={location}
    >
      {/* Keep auth shell while lazy AppLayout loads — avoid thin bar / 2nd shimmer without sidebar. */}
      <Suspense fallback={<AuthRouteFallback />}>{children}</Suspense>
    </ProtectedRouteGate>
  );
}
